<template>
  <div class="altitude-ir-box-panel d-flex flex-column flex-grow-1" style="min-height: 240px">
    <div class="px-2 pt-1 flex-grow-0">
      <v-select
        v-model="selectedId"
        :items="imageItems"
        item-text="text"
        item-value="value"
        dense
        outlined
        hide-details
        :label="$t('altitude.estimateIrSelect')"
        data-testid="altitude-ir-select"
        @change="onImageChange"
      />
      <div class="d-flex align-center mt-1">
        <v-btn-toggle
          v-model="toolMode"
          dense
          mandatory
          class="mr-2"
          data-testid="altitude-ir-tool-mode"
        >
          <v-btn small value="draw" :title="$t('altitude.estimateToolDraw')">
            <v-icon small>mdi-vector-polygon</v-icon>
          </v-btn>
          <v-btn small value="pan" :title="$t('altitude.estimateToolPan')">
            <v-icon small>mdi-hand-back-left</v-icon>
          </v-btn>
        </v-btn-toggle>
        <span class="caption text--secondary">
          {{
            N_px != null
              ? $t('altitude.estimateIrNPx', { n: Math.round(N_px) })
              : $t('altitude.estimateIrDrawHint')
          }}
        </span>
        <v-spacer />
        <v-btn x-small text class="mr-1" data-testid="altitude-ir-zoom-reset" @click="resetView">
          {{ $t('altitude.estimateZoomReset') }}
        </v-btn>
        <v-btn
          x-small
          text
          :disabled="N_px == null && draftPoints.length === 0"
          data-testid="altitude-ir-clear"
          @click="clearBox"
        >
          {{ $t('altitude.estimateClearBox') }}
        </v-btn>
      </div>
    </div>
    <div
      class="flex-grow-1 d-flex align-center justify-center pa-2"
      style="min-height: 200px; position: relative"
      data-testid="altitude-estimate-ir-slot"
    >
      <svg
        v-if="processedUrl"
        ref="svg"
        class="rounded"
        :viewBox="viewBoxStr"
        width="100%"
        :style="{ maxHeight: '360px', background: '#111', touchAction: 'none', cursor: svgCursor }"
        preserveAspectRatio="xMidYMid meet"
        data-testid="altitude-ir-box-svg"
        @wheel.prevent="onWheel"
        @mousedown.prevent="onMouseDown"
        @mousemove.prevent="onMouseMove"
        @mouseup.prevent="onMouseUp"
        @mouseleave="onMouseLeave"
        @contextmenu.prevent="cancelDrawing"
      >
        <image :href="processedUrl" :width="imgW" :height="imgH" preserveAspectRatio="none" />
        <!-- drafting rubber-band (OL-like) -->
        <polygon
          v-if="previewRingPoints"
          :points="previewRingPoints"
          fill="rgba(25, 118, 210, 0.15)"
          stroke="#42a5f5"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
        <polyline
          v-else-if="draftPolylinePoints"
          :points="draftPolylinePoints"
          fill="none"
          stroke="#42a5f5"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
        <polygon
          v-if="closedRing"
          :points="closedRingPoints"
          fill="rgba(25, 118, 210, 0.2)"
          stroke="#42a5f5"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
        <circle
          v-for="(p, i) in draftPoints"
          :key="'pt-' + i"
          :cx="p[0]"
          :cy="p[1]"
          r="3"
          fill="#42a5f5"
          stroke="#fff"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
        />
      </svg>
      <div v-else class="text--secondary caption text-center">
        {{ $t('altitude.estimateIrPlaceholder') }}
      </div>
    </div>
  </div>
</template>

<script>
  import { processImageUrlForCdn } from '@/utils/imagePolygonManager/imageLoader';
  import { longestSidePxFromRing } from '@/features/altitude-estimate/boxMetrics';

  const MIN_ZOOM = 0.4;
  const MAX_ZOOM = 8;
  /** 직사각형: RGB(OL maxPoints:4)와 동일하게 4점에서 자동 완료 */
  const RECT_POINT_COUNT = 4;

  export default {
    name: 'AltitudeIrBoxPanel',
    props: {
      thermalImages: { type: Array, default: () => [] }
    },
    data() {
      return {
        selectedId: null,
        imgW: 640,
        imgH: 512,
        /** @type {Array<[number, number]>} */
        draftPoints: [],
        /** @type {[number, number]|null} */
        cursorPoint: null,
        closedRing: null,
        N_px: null,
        irBox: null,
        viewX: 0,
        viewY: 0,
        viewW: 640,
        viewH: 512,
        panning: false,
        panOrigin: null,
        panViewOrigin: null,
        /** 'draw' | 'pan' */
        toolMode: 'draw',
        spacePanHeld: false
      };
    },
    computed: {
      imageItems() {
        return (this.thermalImages || []).map(img => ({
          text: img.imgName || String(img.id),
          value: img.id
        }));
      },
      selectedImage() {
        return (this.thermalImages || []).find(
          img => Number(img.id) === Number(this.selectedId)
        );
      },
      processedUrl() {
        const url = this.selectedImage?.imgUrl || this.selectedImage?.thumbImgUrl;
        return url ? processImageUrlForCdn(url) : '';
      },
      viewBoxStr() {
        return `${this.viewX} ${this.viewY} ${this.viewW} ${this.viewH}`;
      },
      effectivePanMode() {
        return this.toolMode === 'pan' || this.spacePanHeld;
      },
      svgCursor() {
        if (this.panning) {
          return 'grabbing';
        }
        return this.effectivePanMode ? 'grab' : 'crosshair';
      },
      draftPolylinePoints() {
        if (this.closedRing || this.draftPoints.length === 0) {
          return '';
        }
        const pts = this.draftPoints.slice();
        if (this.cursorPoint) {
          pts.push(this.cursorPoint);
        }
        return pts.map(p => `${p[0]},${p[1]}`).join(' ');
      },
      /** 3점 이상이면 커서까지 포함해 면 미리보기 (첫점으로 닫힘) */
      previewRingPoints() {
        if (this.closedRing || this.draftPoints.length < 2 || !this.cursorPoint) {
          return '';
        }
        const pts = [...this.draftPoints, this.cursorPoint];
        if (pts.length < 3) {
          return '';
        }
        return pts.map(p => `${p[0]},${p[1]}`).join(' ');
      },
      closedRingPoints() {
        if (!this.closedRing) {
          return '';
        }
        return this.closedRing.map(p => `${p[0]},${p[1]}`).join(' ');
      }
    },
    watch: {
      thermalImages: {
        immediate: true,
        handler(list) {
          if (!list?.length) {
            this.selectedId = null;
            this.$emit('image-change', null);
            return;
          }
          if (
            this.selectedId == null ||
            !list.some(i => Number(i.id) === Number(this.selectedId))
          ) {
            this.selectedId = list[0].id;
            this.syncImageSize();
            this.clearBox();
            this.resetView();
            this.$nextTick(() => {
              this.$emit('image-change', this.selectedImage || null);
            });
          }
        }
      }
    },
    mounted() {
      this._onKeyDown = evt => {
        if (evt.key === 'Escape') {
          this.cancelDrawing();
          return;
        }
        if (evt.code === 'Space' && !evt.repeat) {
          evt.preventDefault();
          this.spacePanHeld = true;
        }
      };
      this._onKeyUp = evt => {
        if (evt.code === 'Space') {
          this.spacePanHeld = false;
          if (this.panning) {
            this.onMouseUp();
          }
        }
      };
      window.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('keyup', this._onKeyUp);
    },
    beforeDestroy() {
      if (this._onKeyDown) {
        window.removeEventListener('keydown', this._onKeyDown);
      }
      if (this._onKeyUp) {
        window.removeEventListener('keyup', this._onKeyUp);
      }
    },
    methods: {
      syncImageSize() {
        const img = this.selectedImage;
        this.imgW = Math.max(1, Number(img?.imgWidth) || 640);
        this.imgH = Math.max(1, Number(img?.imgHeight) || 512);
      },

      /** 우클릭·ESC: 그리는 중이면 초안 취소, 아니면 완료 폴리곤 제거 */
      cancelDrawing() {
        if (this.draftPoints.length > 0) {
          this.draftPoints = [];
          this.cursorPoint = null;
          if (!this.closedRing) {
            this.$emit('box-change', null);
          }
          return;
        }
        if (this.closedRing) {
          this.clearBox();
        }
      },

      onImageChange() {
        this.syncImageSize();
        this.clearBox();
        this.resetView();
        this.$emit('image-change', this.selectedImage || null);
      },

      resetView() {
        this.viewX = 0;
        this.viewY = 0;
        this.viewW = this.imgW;
        this.viewH = this.imgH;
      },

      svgPoint(evt) {
        const svg = this.$refs.svg;
        if (!svg) {
          return null;
        }
        const pt = svg.createSVGPoint();
        pt.x = evt.clientX;
        pt.y = evt.clientY;
        const ctm = svg.getScreenCTM();
        if (!ctm) {
          return null;
        }
        const local = pt.matrixTransform(ctm.inverse());
        return {
          x: Math.max(0, Math.min(this.imgW, local.x)),
          y: Math.max(0, Math.min(this.imgH, local.y))
        };
      },

      onWheel(evt) {
        const p = this.svgPoint(evt);
        if (!p) {
          return;
        }
        const factor = evt.deltaY < 0 ? 0.85 : 1.15;
        const nextW = Math.min(
          this.imgW / MIN_ZOOM,
          Math.max(this.imgW / MAX_ZOOM, this.viewW * factor)
        );
        const nextH = nextW * (this.imgH / this.imgW);
        const scale = nextW / this.viewW;
        let nextX = p.x - (p.x - this.viewX) * scale;
        let nextY = p.y - (p.y - this.viewY) * scale;
        nextX = Math.min(Math.max(0, nextX), Math.max(0, this.imgW - nextW));
        nextY = Math.min(Math.max(0, nextY), Math.max(0, this.imgH - nextH));
        this.viewW = nextW;
        this.viewH = nextH;
        this.viewX = nextX;
        this.viewY = nextY;
      },

      onMouseDown(evt) {
        // 손 모드 / Space / 중버튼 / Shift+왼쪽 → 팬
        if (
          evt.button === 1 ||
          (evt.button === 0 && (this.effectivePanMode || evt.shiftKey))
        ) {
          this.panning = true;
          this.panOrigin = { x: evt.clientX, y: evt.clientY };
          this.panViewOrigin = { x: this.viewX, y: this.viewY };
          return;
        }
        if (evt.button !== 0) {
          return;
        }
        if (this.effectivePanMode) {
          return;
        }
        const p = this.svgPoint(evt);
        if (!p) {
          return;
        }
        if (this.closedRing) {
          this.closedRing = null;
          this.N_px = null;
          this.irBox = null;
          this.draftPoints = [];
          this.$emit('box-change', null);
        }
        this.draftPoints = [...this.draftPoints, [p.x, p.y]];
        this.cursorPoint = [p.x, p.y];
        if (this.draftPoints.length >= RECT_POINT_COUNT) {
          this.finishPolygon(this.draftPoints);
        }
      },

      onMouseMove(evt) {
        if (this.panning && this.panOrigin && this.panViewOrigin) {
          const svg = this.$refs.svg;
          if (!svg) {
            return;
          }
          const rect = svg.getBoundingClientRect();
          if (!rect.width || !rect.height) {
            return;
          }
          const dx = ((evt.clientX - this.panOrigin.x) / rect.width) * this.viewW;
          const dy = ((evt.clientY - this.panOrigin.y) / rect.height) * this.viewH;
          let nextX = this.panViewOrigin.x - dx;
          let nextY = this.panViewOrigin.y - dy;
          nextX = Math.min(Math.max(0, nextX), Math.max(0, this.imgW - this.viewW));
          nextY = Math.min(Math.max(0, nextY), Math.max(0, this.imgH - this.viewH));
          this.viewX = nextX;
          this.viewY = nextY;
          return;
        }
        if (this.closedRing || this.draftPoints.length === 0) {
          this.cursorPoint = null;
          return;
        }
        const p = this.svgPoint(evt);
        this.cursorPoint = p ? [p.x, p.y] : null;
      },

      onMouseUp() {
        this.panning = false;
        this.panOrigin = null;
        this.panViewOrigin = null;
      },

      onMouseLeave() {
        this.onMouseUp();
        this.cursorPoint = null;
      },

      /**
       * @param {Array<[number, number]>} [points]
       */
      finishPolygon(points) {
        const ringSrc = points || this.draftPoints;
        if (!ringSrc || ringSrc.length < 3) {
          return;
        }
        const ring = ringSrc.map(p => [p[0], p[1]]);
        const N_px = longestSidePxFromRing(ring);
        if (N_px == null || N_px < 1) {
          this.clearBox();
          return;
        }
        this.closedRing = ring;
        this.draftPoints = [];
        this.cursorPoint = null;
        this.N_px = N_px;
        this.irBox = {
          ring,
          N_px,
          imgWidth: this.imgW,
          imgHeight: this.imgH
        };
        this.$emit('box-change', {
          irBox: this.irBox,
          image: this.selectedImage || null
        });
      },

      clearBox() {
        this.draftPoints = [];
        this.cursorPoint = null;
        this.closedRing = null;
        this.N_px = null;
        this.irBox = null;
        this.$emit('box-change', null);
      },

      reset() {
        this.clearBox();
      }
    }
  };
</script>
